import { Network, truncateAddress } from "@aptos-labs/ts-sdk";
import { IconCaretDown, IconSignOut } from "@decibeltrade/react-icons";
import { getPrimarySubaccountAddr } from "@decibeltrade/sdk";
import { Button } from "@decibeltrade/web-core/components";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { createElement, JSX, useEffect, useMemo, useRef, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import {
  ACCOUNT_RECONNECT_DELAY_MS,
  ACCOUNT_RECONNECT_TIMEOUT_MS,
  SELECTED_SUBACCOUNT_ITEM_KEY,
} from "@/constants";
import { sdkConfig } from "@/contexts/dex-sdk";
import { userOwnedVaultsQuery } from "@/contexts/queries/vault/user-owned-vaults";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { getLocalStorageItem, setLocalStorageItem } from "@/hooks/storage";
import { useAuthState } from "@/hooks/use-auth-state";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { useDelegatedSubaccounts } from "@/hooks/use-delegated-subaccounts";
import { useGetImportedSessionKeys } from "@/hooks/use-imported-session-keys";
import { KeylessInfo } from "@/hooks/use-keyless-accounts";
import { useLogout } from "@/hooks/use-logout";
import { useSubaccountsWithPrimary } from "@/hooks/use-subaccounts-with-primary";
import { Placement } from "@/types/ui";
import { addressesEqual } from "@/utils/address";

import { CopyButton } from "../buttons/copy-button";
import { Clickable } from "../clickable";
import { openWalletSelectorModal } from "../modal/wallet-selector-modal";
import { Popover } from "../popover";
import { Skeleton } from "../skeleton";
import { Tag } from "../tag";
import { Tooltip } from "../tooltip";
import { getKeylessProviderIcon } from "../wallet-selector/keyless-button";

interface AccountDropdownProps extends MenuCustomization {
  subaccountAddress: string;
  renderTrigger?: (item: { address: string; label: string }) => JSX.Element;
  placement?: Placement;
}

/** Lets a host restyle the menu without changing the terminal's defaults. */
interface MenuCustomization {
  /** Class for the menu container; its copy and Switch buttons expose data attributes to target. */
  popoverClassName?: string;
  /** Replaces the "Wallet connection:" label. */
  walletLabel?: string;
}

interface SelectedSubaccountItem {
  address: string;
  label: string;
}

const generateLsKey = (ownerAccount: string) => `${SELECTED_SUBACCOUNT_ITEM_KEY}-${ownerAccount}`;
const getCachedSelectedSubaccount = (ownerAccount: string) => {
  const stringifiedJson = getLocalStorageItem(generateLsKey(ownerAccount.toLowerCase()));
  if (!stringifiedJson) return null;
  return JSON.parse(stringifiedJson) as SelectedSubaccountItem | null;
};

const setCachedSelectedSubaccount = (ownerAccount: string, item: SelectedSubaccountItem) => {
  setLocalStorageItem(generateLsKey(ownerAccount.toLowerCase()), item);
};

const AccountDropdownContentIfWalletConnected = ({
  subaccountAddress,
  renderTrigger,
  placement = "bottom-start",
  popoverClassName,
  walletLabel,
}: AccountDropdownProps) => {
  const { account, chainName } = useDecibelWallet();
  const accountAddress = account?.address.toString();
  const { subaccounts, isLoadingSubaccounts } = useSubaccountsWithPrimary();
  const { data: userVaultsData, isPending: isLoadingUserVaults } = useQuery({
    ...userOwnedVaultsQuery({ ownerAddr: accountAddress ?? "" }),
    enabled: !!accountAddress,
  });
  const userVaults = userVaultsData?.items ?? [];
  const delegatedSubaccounts = useDelegatedSubaccounts(accountAddress);
  const { connectedVault } = useWriteDexSdk();
  const formattedAccountAddress = account?.ansName ?? truncateAddress(accountAddress ?? "");

  const selectedItem: SelectedSubaccountItem | null = useMemo(() => {
    if (!account) return null;

    const connectedSubaccount = subaccounts.find((subaccount) =>
      addressesEqual(subaccount.subaccount_address, subaccountAddress),
    );

    if (connectedSubaccount) {
      return {
        address: connectedSubaccount.subaccount_address,
        label: connectedSubaccount.custom_label,
      };
    }

    if (connectedVault) {
      // TODO: find out if we should show the vault's address or the vault's subaccount address
      return {
        address: getPrimarySubaccountAddr(
          connectedVault.vault_address,
          sdkConfig.compatVersion,
          sdkConfig.deployment.package,
        ),
        label: connectedVault.vault_name,
      };
    }

    const delegatedSubaccount = delegatedSubaccounts.find((v) =>
      addressesEqual(v.subaccountAddress, subaccountAddress),
    );
    if (delegatedSubaccount) {
      return { address: delegatedSubaccount.subaccountAddress, label: delegatedSubaccount.label };
    }

    return getCachedSelectedSubaccount(account.address.toString());
  }, [connectedVault, delegatedSubaccounts, subaccounts, subaccountAddress, account]);

  useEffect(() => {
    if (!account || !selectedItem) return;
    setCachedSelectedSubaccount(account.address.toString(), selectedItem);
  }, [selectedItem, account]);

  // This should never happen, but we'll handle it just in case
  if (!selectedItem) {
    return <SkeletonWithFallback />;
  }

  return (
    <Popover
      trigger={
        renderTrigger ? renderTrigger(selectedItem) : <ConnectedMenuTrigger item={selectedItem} />
      }
      placement={placement}
    >
      {({ setIsOpen }) => (
        <div className={cx(popoverContainerStyles, popoverClassName)} data-account-menu>
          <WalletConnectionItem
            address={formattedAccountAddress}
            setIsOpen={setIsOpen}
            chainName={chainName}
            label={walletLabel}
          />
          {isLoadingSubaccounts ? (
            <>
              <AccountItemSkeleton />
              <AccountItemSkeleton />
            </>
          ) : (
            <>
              {subaccounts.map((subaccount) => (
                <AccountItem
                  key={subaccount.subaccount_address}
                  item={{
                    address: subaccount.subaccount_address,
                    label: subaccount.custom_label,
                  }}
                  setIsOpen={setIsOpen}
                />
              ))}
            </>
          )}
          {isLoadingUserVaults ? (
            <>
              <AccountItemSkeleton />
              <AccountItemSkeleton />
            </>
          ) : (
            <>
              {userVaults.map((vault) => (
                <AccountItem
                  key={vault.subaccount_address}
                  item={{ address: vault.subaccount_address, label: vault.vault_name }}
                  isVault
                  setIsOpen={setIsOpen}
                />
              ))}
            </>
          )}
          {delegatedSubaccounts.map((delegated) => (
            <AccountItem
              key={delegated.subaccountAddress}
              item={{ address: delegated.subaccountAddress, label: delegated.label }}
              isDelegated
              setIsOpen={setIsOpen}
            />
          ))}
        </div>
      )}
    </Popover>
  );
};

const AccountDropdownContentIfImportedSessionKey = ({
  subaccountAddress,
  renderTrigger,
  placement = "bottom-start",
  popoverClassName,
  walletLabel,
}: AccountDropdownProps) => {
  const importedAccounts = useGetImportedSessionKeys();

  const currentImportedAccount = importedAccounts.find((account) =>
    addressesEqual(account.subaccountAddress, subaccountAddress),
  );

  if (!currentImportedAccount) return null;

  return (
    <Popover
      trigger={
        renderTrigger ? (
          renderTrigger({ address: subaccountAddress, label: currentImportedAccount.label ?? "" })
        ) : (
          <ConnectedMenuTrigger
            item={{ address: subaccountAddress, label: currentImportedAccount.label ?? "" }}
          />
        )
      }
      placement={placement}
    >
      {({ setIsOpen }) => (
        <div className={cx(popoverContainerStyles, popoverClassName)} data-account-menu>
          <WalletConnectionItem
            address={truncateAddress(
              currentImportedAccount.ownerAddress ?? currentImportedAccount.accountAddress,
            )}
            setIsOpen={setIsOpen}
            label={walletLabel}
          />

          {importedAccounts.map((subaccount) => (
            <AccountItem
              key={subaccount.subaccountAddress}
              item={{
                address: subaccount.subaccountAddress,
                label: subaccount.label ?? "",
              }}
              setIsOpen={setIsOpen}
            />
          ))}
        </div>
      )}
    </Popover>
  );
};

export const AccountDropdown = ({
  subaccountAddress,
  renderTrigger,
  placement,
  popoverClassName,
  walletLabel,
}: AccountDropdownProps) => {
  const t = useTranslations("general");
  const { state } = useAuthState();

  const currentNetwork = sdkConfig.network;
  const isTestnet = currentNetwork === Network.TESTNET;

  const dropdown =
    state === "SESSION_KEY_IMPORTED" ? (
      <AccountDropdownContentIfImportedSessionKey
        subaccountAddress={subaccountAddress}
        renderTrigger={renderTrigger}
        placement={placement}
        popoverClassName={popoverClassName}
        walletLabel={walletLabel}
      />
    ) : (
      <AccountDropdownContentIfWalletConnected
        subaccountAddress={subaccountAddress}
        renderTrigger={renderTrigger}
        placement={placement}
        popoverClassName={popoverClassName}
        walletLabel={walletLabel}
      />
    );

  if (renderTrigger) {
    return dropdown;
  }

  return (
    <div
      className={flex({
        flexDirection: { base: "column-reverse", desktop: "row" },
        align: { base: "flex-start", desktop: "center" },
        gap: { base: "4", desktop: "8" },
        mt: { desktopDown: isTestnet ? "-8" : undefined },
      })}
    >
      {isTestnet && (
        <Tag variant="gold" className={css({ desktopDown: { mt: "-8" } })}>
          {t("testnet")}
        </Tag>
      )}
      {dropdown}
    </div>
  );
};

interface AccountItem {
  address: string;
  label: string;
}

interface ConnectedMenuTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  ref?: React.Ref<HTMLButtonElement>;
  item: AccountItem;
}

const ConnectedMenuTrigger = ({ ref, className, item, ...rest }: ConnectedMenuTriggerProps) => {
  const formattedAddress = truncateAddress(item.address);
  const t = useTranslations("accounts");

  return (
    <Button
      ref={ref}
      variant="text"
      TailIcon={IconCaretDown}
      className={cx(connectedMenuStyles, className)}
      ariaLabel={t("openAccountDropdown")}
      {...rest}
    >
      <div className={flex({ align: "center", gap: "8" })}>
        <p
          className={css({
            hideBelow: "sm",
            textStyle: "highlight.md",
            color: "text.secondary",
            truncate: true,
            maxW: "160",
          })}
        >
          {item.label}
        </p>

        <p
          className={css({
            textStyle: "highlight.lg",
            desktop: { textStyle: "highlight.md" },
            color: "text.tertiary",
          })}
        >
          {formattedAddress}
        </p>
      </div>
    </Button>
  );
};

interface WalletConnectionItemProps {
  address: string;
  setIsOpen: (isOpen: boolean) => void;
  chainName?: string;
  label?: string;
}

const WalletConnectionItem = ({
  address,
  setIsOpen,
  chainName,
  label,
}: WalletConnectionItemProps) => {
  const t = useTranslations("accountControls");
  const { disconnect } = useWriteDexSdk();
  const { state, keylessInfo } = useAuthState();

  const handleDisconnect = () => {
    disconnect();
    setIsOpen(false);
  };

  return (
    <div className={connectionItemContainerStyles}>
      <div className={connectionItemInnerStyles}>
        {state === "WALLET_CONNECTED" && (
          <Clickable onClick={handleDisconnect} aria-label={t("disconnect")}>
            <div className={flex({ align: "center", gap: "8", w: "max" })}>
              <p className={css({ color: "text.secondary" })}>{t("disconnect")}</p>
              <IconSignOut className={css({ color: "text.tertiary", w: "16", h: "16" })} />
            </div>
          </Clickable>
        )}

        <div className={flex({ align: "center", gap: "8" })}>
          <p className={css({ color: "text.tertiary" })}>{label ?? t("walletConnection")}</p>
          <p className={css({ color: "text.tertiary" })}>
            {address}
            {chainName ? ` (${chainName})` : ""}
          </p>
          {keylessInfo ? <KeylessInfoIcon keylessInfo={keylessInfo} /> : null}
        </div>
      </div>
    </div>
  );
};

const KeylessInfoIcon = ({ keylessInfo }: { keylessInfo: KeylessInfo }) => (
  <Tooltip content={keylessInfo.email} disabled={!keylessInfo.email}>
    <div className={flex({ align: "center", gap: "8" })}>
      {createElement(getKeylessProviderIcon(keylessInfo.provider), {
        className: css({ w: "12", h: "12" }),
      })}
    </div>
  </Tooltip>
);

interface AccountItemProps {
  item: AccountItem;
  setIsOpen: (isOpen: boolean) => void;
  isVault?: boolean;
  isDelegated?: boolean;
}

const AccountItem = ({ item, setIsOpen, isVault, isDelegated }: AccountItemProps) => {
  const t = useTranslations("accountControls");
  const logout = useLogout();
  const { subaccountAddress, state } = useAuthState();
  const { switchSubaccountAddress } = useWriteDexSdk();

  const formattedAddress = truncateAddress(item.address);
  const isSelected = addressesEqual(item.address, subaccountAddress);

  const handleSwitchAddress = () => {
    switchSubaccountAddress(item.address);
    setIsOpen(false);
  };

  return (
    <div className={popoverItemStyles}>
      <div className={stack({ gap: "2", minW: "0", flex: "1" })}>
        <div className={flex({ gap: "8", align: "center", minW: "0" })}>
          <p className={css({ color: "text.secondary", truncate: true })}>{item.label}</p>
          {isVault && (
            <Tag variant="gold" className={noShrinkStyles}>
              {t("vault")}
            </Tag>
          )}
          {isDelegated && (
            <Tag variant="gold" className={noShrinkStyles}>
              {t("delegated")}
            </Tag>
          )}
        </div>

        <p className={css({ color: "text.tertiary" })}>{formattedAddress}</p>
      </div>

      <div className={flex({ align: "center", gap: "4", flexShrink: "0" })}>
        {!isSelected && (
          <Button
            data-account-switch
            size="sm"
            variant="secondary"
            className={css({ color: "text.tertiary" })}
            onClick={handleSwitchAddress}
          >
            {t("switch")}
          </Button>
        )}

        {state === "SESSION_KEY_IMPORTED" && isSelected && (
          <Clickable onClick={logout} aria-label={t("disconnect")}>
            <IconSignOut className={css({ color: "text.tertiary", w: "16", h: "16" })} />
          </Clickable>
        )}

        <CopyButton
          data-account-copy
          value={item.address}
          size="sm"
          variant="secondary"
          warningMessage={t("subaccountCopyWarning")}
          className={css({
            "& svg": { color: "text.tertiary", _hover: { color: "text.secondary" } },
          })}
        />
      </div>
    </div>
  );
};

// Show the skeleton for a bit and then fallback to a reconnect button if the subaccount is not found
// This may never happen, but we'll handle it just in case
const SkeletonWithFallback = () => {
  const [isWaiting, setIsWaiting] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { disconnect } = useWriteDexSdk();
  const t = useTranslations("accounts");

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsWaiting(false);
    }, ACCOUNT_RECONNECT_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleReconnect = () => {
    disconnect();
    setTimeout(() => {
      openWalletSelectorModal();
    }, ACCOUNT_RECONNECT_DELAY_MS);
  };

  if (isWaiting) {
    return <Skeleton className={css({ w: "192", h: "32", rounded: "300" })} />;
  }

  return (
    <Button variant="primary" onClick={handleReconnect}>
      {t("reconnect")}
    </Button>
  );
};

const connectedMenuStyles = css({
  px: "0!",
  "&[data-state='open'] svg": {
    transform: "rotate(180deg)",
  },
  "&[data-state='closed'] svg": {
    transform: "rotate(0deg)",
  },
  "& svg": {
    transitionPreset: "transform",
  },
});

const popoverContainerStyles = stack({
  gap: "0",
  pb: "4",
  bg: "background.primary",
  rounded: "300",
  minW: "288",
  maxH: "480",
  overflowY: "auto",
  shadow: "default",
});

const connectionItemContainerStyles = stack({
  p: "4",
  bg: "background.primary",
  position: "sticky",
  rounded: "200",
  top: "0",
  zIndex: "[1]",
});

const connectionItemInnerStyles = stack({
  gap: "2",
  w: "full",
  bg: "background.secondary",
  rounded: "200",
  py: "4",
  px: "8",
});

const AccountItemSkeleton = () => {
  return (
    <div className={popoverItemStyles}>
      <div className={stack({ gap: "2", w: "max" })}>
        <Skeleton className={css({ w: "120", h: "16", rounded: "200" })} />
        <Skeleton className={css({ w: "160", h: "14", rounded: "200" })} />
      </div>
      <div className={flex({ align: "center", gap: "4" })}>
        <Skeleton className={css({ w: "64", h: "28", rounded: "200" })} />
        <Skeleton className={css({ w: "28", h: "28", rounded: "200" })} />
      </div>
    </div>
  );
};

const popoverItemStyles = flex({
  w: "full",
  align: "center",
  justifyContent: "space-between",
  gap: "8",
  py: "4",
  px: "12",
});

const noShrinkStyles = css({ flexShrink: "0" });
